import React, { createContext, useState, useEffect, useCallback } from "react";
import type { User, Organization, AuthTokens } from "../types";
import { authApi } from "../api/auth";

interface AuthContextValue {
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  organization: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

const TOKEN_KEY = "acts_token";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      try {
        const tokens: AuthTokens = JSON.parse(stored);
        setUser(tokens.user);
        setOrganization(tokens.organization ?? null);
        // Optionally validate token by hitting /me
        authApi
          .me()
          .then((me) => setUser(me))
          .catch(() => {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            setOrganization(null);
          })
          .finally(() => setLoading(false));
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await authApi.login(email, password);
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    setUser(tokens.user);
    setOrganization(tokens.organization ?? null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setOrganization(null);
  }, []);

  // Auto-logout when the axios interceptor detects an expired/invalid token
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setOrganization(null);
    };
    window.addEventListener("auth:expired", handler);
    return () => window.removeEventListener("auth:expired", handler);
  }, []);

  return (
    <AuthContext.Provider value={{ user, organization, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function getStoredToken(): string | null {
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return null;
    const tokens: AuthTokens = JSON.parse(stored);
    return tokens.accessToken;
  } catch {
    return null;
  }
}

export function getStoredOrgSlug(): string | null {
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return null;
    const tokens: AuthTokens = JSON.parse(stored);
    return tokens.organization?.slug ?? null;
  } catch {
    return null;
  }
}
